import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import HearingIcon from "@material-ui/icons/Hearing";
import MicIcon from "@material-ui/icons/Mic";
import NoteIcon from "@material-ui/icons/Note";
import { buildRawListeningOwner } from "../../listenings/listeningOwners";
import ActionCountCell from "../../shared/ActionCountCell";
import { formatAppVolume } from "../../shared/appVolumeUtils";
import MicrophoneCalibrationStatus, {
  microphoneCalibrationColumnStyle,
} from "../../shared/MicrophoneCalibrationStatus";
import { convertDate } from "../../utils/dateConverter";
import {
  formatMicrophoneSignalPower,
  getMicrophoneFrequencyCalibration,
  hasMicrophoneCalibration,
  resolveMicrophoneCalibration,
} from "../../shared/microphoneCalibrationUtils";

function renderMissingCalibrationCell(colSpan) {
  return (
    <TableCell colSpan={colSpan}>
      Brak danych kalibracyjnych dla tego komponentu.
    </TableCell>
  );
}

function renderCheckboxCell(componentId, checkBoxes, handleCheckboxChange) {
  return (
    <TableCell>
      <input
        type="checkbox"
        checked={checkBoxes[componentId] ?? false}
        onChange={() => handleCheckboxChange(componentId)}
      />
    </TableCell>
  );
}

function renderIndexAndCheckboxCells(
  component,
  index,
  checkBoxes,
  handleCheckboxChange,
) {
  const componentId = component.deviceCalibration.id;

  return (
    <>
      <TableCell>{index + 1}</TableCell>
      {renderCheckboxCell(componentId, checkBoxes, handleCheckboxChange)}
    </>
  );
}

function renderListeningCountCell(deviceCalibration, handleOpenListenings) {
  const listeningCount = deviceCalibration?.listeningCount ?? 0;
  const listeningOwner = buildRawListeningOwner(deviceCalibration);

  return (
    <ActionCountCell
      label="Odsłuchy"
      icon={HearingIcon}
      onClick={() => handleOpenListenings(listeningOwner)}
      count={listeningCount}
      disabled={!deviceCalibration?.id}
    />
  );
}

function renderRawNotesCountCell(
  deviceCalibration,
  handleOpenRawNotes,
  getRawNotesCount,
) {
  const notesCount = getRawNotesCount
    ? getRawNotesCount(deviceCalibration, deviceCalibration?.notesCount ?? 0)
    : deviceCalibration?.notesCount ?? 0;

  return (
    <ActionCountCell
      label="Notatki"
      icon={NoteIcon}
      onClick={() => handleOpenRawNotes(deviceCalibration)}
      count={notesCount}
      disabled={!deviceCalibration?.id}
    />
  );
}

function checkIfNullable(item) {
  return item ?? "Brak Danych";
}

function checkIfNullableAndRound(item) {
  if (item == null) {
    return "Brak Danych";
  }

  if (typeof item !== "number" || isNaN(item)) {
    return "Brak Danych";
  }

  return Math.round(item * 10) / 10;
}

const PHONE_SPEAKER_FREQUENCIES = [500, 1000, 2000, 4000];
const EXTENDED_FREQUENCIES = [500, 1000, 2000, 4000, 8000, 16000];
const HIGHER_LEVEL_COMPACT_COLUMN_WIDTHS = [
  "4%",
  "10%",
  "18%",
  "14%",
  "14%",
  "14%",
  "14%",
  "12%",
];
const HIGHER_LEVEL_DETAILED_COLUMN_WIDTHS = [
  "4%",
  "10%",
  "18%",
  "16%",
  "11%",
  "11%",
  "11%",
  "11%",
  "8%",
];
const HIGHER_LEVEL_DETAILED_VALUE_COLUMN_WIDTHS = [
  "26.6666667%",
  "18.3333333%",
  "18.3333333%",
  "18.3333333%",
  "18.3333333%",
];
const MODEL_COMPACT_COLUMN_WIDTHS = [
  "4%",
  "10%",
  "14%",
  "14%",
  "12%",
  "12%",
  "12%",
  "12%",
  "5%",
  "5%",
];
const MODEL_DETAILED_COLUMN_WIDTHS = [
  "4%",
  "10%",
  "14%",
  "14%",
  "8%",
  "8%",
  "8%",
  "8%",
  "8%",
  "8%",
  "5%",
  "5%",
];
const MODEL_DETAILED_VALUE_COLUMN_WIDTHS = [
  "22.5806452%",
  "12.9032258%",
  "12.9032258%",
  "12.9032258%",
  "12.9032258%",
  "12.9032258%",
  "12.9032258%",
];

const DETAILED_ROWS = [
  { label: "Głośność aplikacji", name: "appVolume" },
  { label: "Głośność główna", name: "masterVolume" },
  {
    label: "Testowane względem [dB SPL]",
    name: "intensityTestedAgainst",
  },
  { label: "Różnica", name: "difference" },
  {
    label: "Maksymalna głośność aplikacji [dB SPL]",
    name: "maxIntensity",
  },
  { label: "Siła sygnału mikrofonu", name: "measuredSignalPower" },
];

export function getDetailedValue(
  rowName,
  calibration,
  frequency,
  calibrationIndex,
  mainCalibrations,
  microphoneCalibration,
) {
  if (rowName === "measuredSignalPower") {
    const microphoneFrequencyCalibration = getMicrophoneFrequencyCalibration(
      microphoneCalibration,
      frequency,
    );

    return formatMicrophoneSignalPower(
      microphoneFrequencyCalibration?.measuredSignalPower,
    );
  }

  if (!calibration) {
    return "Brak Danych";
  }

  if (rowName === "difference") {
    return mainCalibrations?.[calibrationIndex] !== undefined
      ? formatAppVolume(
          calibration.appVolume - mainCalibrations[calibrationIndex],
        )
      : "Brak Danych";
  }

  if (rowName === "maxIntensity") {
    return checkIfNullableAndRound(calibration.maxIntensity);
  }

  if (rowName === "appVolume") {
    return formatAppVolume(calibration.appVolume);
  }

  return checkIfNullable(calibration[rowName]);
}

function renderDetailedCalibrationsTable(
  calibrations,
  mainCalibrations,
  microphoneCalibration,
  frequencies,
  includeMicrophoneData,
  columnWidths,
) {
  const calibrationsByFrequency = new Map(
    calibrations.map((calibration) => [Number(calibration.freq), calibration]),
  );

  return (
    <TableCell colSpan={frequencies.length + 1}>
      <Table
        size="small"
        aria-label="Szczegółowe wartości kalibracji"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          {columnWidths.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell>Parametr</TableCell>
            {frequencies.map((frequency) => (
              <TableCell key={frequency} align="right">
                {frequency} Hz
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {DETAILED_ROWS.filter(
            (row) =>
              includeMicrophoneData || row.name !== "measuredSignalPower",
          ).map((row) => (
            <TableRow key={row.name}>
              <TableCell component="th" scope="row">
                {row.label}
              </TableCell>
              {frequencies.map((frequency, calibrationIndex) => (
                <TableCell key={frequency} align="right">
                  {getDetailedValue(
                    row.name,
                    calibrationsByFrequency.get(frequency),
                    frequency,
                    calibrationIndex,
                    mainCalibrations,
                    microphoneCalibration,
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCell>
  );
}

function renderCompactCalibrationCell(calibration, frequency) {
  return (
    <TableCell key={frequency}>
      {calibration
        ? checkIfNullableAndRound(calibration.maxIntensity) + " dB SPL"
        : "Brak Danych"}
    </TableCell>
  );
}

function renderCalibrationsCells(
  deviceCalibration,
  mainCalibrations,
  isDetailedData,
  microphoneCalibration,
  includeMicrophoneData,
) {
  const calibrations = Array.isArray(deviceCalibration?.calibrations)
    ? deviceCalibration.calibrations.slice().sort((a, b) => a.freq - b.freq)
    : null;

  const frequencies =
    isDetailedData && includeMicrophoneData
      ? EXTENDED_FREQUENCIES
      : PHONE_SPEAKER_FREQUENCIES;

  const resolvedMicrophoneCalibration = includeMicrophoneData
    ? resolveMicrophoneCalibration(microphoneCalibration, deviceCalibration)
    : null;

  if (!calibrations || calibrations.length === 0) {
    return (
      <TableCell
        colSpan={frequencies.length + (isDetailedData ? 1 : 0)}
        style={{ textAlign: "center" }}
      >
        Brak Danych
      </TableCell>
    );
  }

  if (isDetailedData) {
    return renderDetailedCalibrationsTable(
      calibrations,
      mainCalibrations,
      resolvedMicrophoneCalibration,
      frequencies,
      includeMicrophoneData,
      includeMicrophoneData
        ? MODEL_DETAILED_VALUE_COLUMN_WIDTHS
        : HIGHER_LEVEL_DETAILED_VALUE_COLUMN_WIDTHS,
    );
  }

  const calibrationsByFrequency = new Map(
    calibrations.map((calibration) => [Number(calibration.freq), calibration]),
  );
  return frequencies.map((frequency) =>
    renderCompactCalibrationCell(
      calibrationsByFrequency.get(frequency),
      frequency,
    ),
  );
}

function renderSharedCalibrationCells(
  component,
  mainCalibrations,
  isDetailedData,
  includeMicrophoneData = false,
) {
  const deviceCalibration =
    component?.deviceCalibration ??
    component?.averageDeviceCalibration ??
    component?.calibration;

  return renderCalibrationsCells(
    deviceCalibration,
    mainCalibrations,
    isDetailedData,
    includeMicrophoneData
      ? resolveMicrophoneCalibration(component, deviceCalibration)
      : null,
    includeMicrophoneData,
  );
}

export const baseConfig = {
  mainRowCells: [
    (data) => (
      <TableCell key="base">
        {data.components[0]?.deviceCalibration?.base
          ? data.components[0].deviceCalibration.base
          : null}
      </TableCell>
    ),
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Producent",
    "500Hz",
    "1000Hz",
    "2000Hz",
    "4000Hz",
    "",
  ],
  detailedCollapsibleHeaders: [
    "#",
    "Aktywna",
    "Producent",
    { label: "Wartości kalibracji", colSpan: 5 },
    "",
  ],
  getColumnWidths: ({ isDetailedData }) =>
    isDetailedData
      ? HIGHER_LEVEL_DETAILED_COLUMN_WIDTHS
      : HIGHER_LEVEL_COMPACT_COLUMN_WIDTHS,
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData },
  ) => {
    if (!component.deviceCalibration) {
      return renderMissingCalibrationCell(isDetailedData ? 9 : 8);
    }

    return (
      <>
        {renderIndexAndCheckboxCells(
          component,
          index,
          checkBoxes,
          handleCheckboxChange,
        )}
        <TableCell>
          {checkIfNullable(component.deviceCalibration.manufacturer)}
        </TableCell>
        {renderSharedCalibrationCells(
          component,
          mainCalibrations,
          isDetailedData,
          false,
        )}
        <TableCell aria-hidden="true" />
      </>
    );
  },
};

export const modelConfig = {
  mainRowCells: [
    (data) => <TableCell key="manufacturer">{data.manufacturer}</TableCell>,
    (data) => <TableCell key="model">{data.model}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Data",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
    "Notatki",
    "Odsłuchy",
  ],
  detailedCollapsibleHeaders: [
    "#",
    "Aktywna",
    "Data",
    { label: "Wartości kalibracji", colSpan: 7 },
    "Notatki",
    "Odsłuchy",
  ],
  getColumnWidths: ({ isDetailedData }) =>
    isDetailedData
      ? MODEL_DETAILED_COLUMN_WIDTHS
      : MODEL_COMPACT_COLUMN_WIDTHS,
  getCollapsibleHeaders: ({ isDetailedData }) =>
    isDetailedData
      ? modelConfig.detailedCollapsibleHeaders
      : [
          "#",
          "Aktywna",
          "Data",
          {
            align: "center",
            style: microphoneCalibrationColumnStyle,
            label: (
              <MicIcon
                titleAccess="Kalibracja mikrofonu"
                aria-label="Kalibracja mikrofonu"
              />
            ),
          },
          "500 hz",
          "1000 hz",
          "2000 hz",
          "4000 hz",
          "Notatki",
          "Odsłuchy",
        ],
  collapsibleRowRender: (
    component,
    index,
    {
      mainCalibrations,
      checkBoxes,
      handleCheckboxChange,
      isDetailedData,
      handleOpenListenings,
      handleOpenRawNotes,
      getRawNotesCount,
    },
  ) => {
    if (!component.deviceCalibration) {
      return renderMissingCalibrationCell(isDetailedData ? 12 : 10);
    }

    return (
      <>
        {renderIndexAndCheckboxCells(
          component,
          index,
          checkBoxes,
          handleCheckboxChange,
        )}

        <TableCell>
          {convertDate(component.deviceCalibration.createdAt)}
        </TableCell>

        {!isDetailedData && (
          <TableCell align="center" style={microphoneCalibrationColumnStyle}>
            <MicrophoneCalibrationStatus
              available={hasMicrophoneCalibration(component)}
            />
          </TableCell>
        )}

        {renderSharedCalibrationCells(
          component,
          mainCalibrations,
          isDetailedData,
          true,
        )}

        {renderRawNotesCountCell(
          component.deviceCalibration,
          handleOpenRawNotes,
          getRawNotesCount,
        )}

        {renderListeningCountCell(
          component.deviceCalibration,
          handleOpenListenings,
        )}
      </>
    );
  },
};

export const brandConfig = {
  mainRowCells: [
    (data) => <TableCell key="brand">{data.brand}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Model",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
    "",
  ],
  detailedCollapsibleHeaders: [
    "#",
    "Aktywna",
    "Model",
    { label: "Wartości kalibracji", colSpan: 5 },
    "",
  ],
  getColumnWidths: ({ isDetailedData }) =>
    isDetailedData
      ? HIGHER_LEVEL_DETAILED_COLUMN_WIDTHS
      : HIGHER_LEVEL_COMPACT_COLUMN_WIDTHS,
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData },
  ) => {
    if (!component.deviceCalibration) {
      return renderMissingCalibrationCell(isDetailedData ? 9 : 8);
    }

    return (
      <>
        {renderIndexAndCheckboxCells(
          component,
          index,
          checkBoxes,
          handleCheckboxChange,
        )}
        <TableCell>{component.deviceCalibration.model}</TableCell>
        {renderSharedCalibrationCells(
          component,
          mainCalibrations,
          isDetailedData,
          false,
        )}
        <TableCell aria-hidden="true" />
      </>
    );
  },
};

export const manufacturerConfig = {
  mainRowCells: [
    (data) => <TableCell key="manufacturer">{data.manufacturer}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Marka",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
    "",
  ],
  detailedCollapsibleHeaders: [
    "#",
    "Aktywna",
    "Marka",
    { label: "Wartości kalibracji", colSpan: 5 },
    "",
  ],
  getColumnWidths: ({ isDetailedData }) =>
    isDetailedData
      ? HIGHER_LEVEL_DETAILED_COLUMN_WIDTHS
      : HIGHER_LEVEL_COMPACT_COLUMN_WIDTHS,
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData },
  ) => {
    if (!component.deviceCalibration) {
      return renderMissingCalibrationCell(isDetailedData ? 9 : 8);
    }

    return (
      <>
        {renderIndexAndCheckboxCells(
          component,
          index,
          checkBoxes,
          handleCheckboxChange,
        )}
        <TableCell>{component.deviceCalibration.brand}</TableCell>
        {renderSharedCalibrationCells(
          component,
          mainCalibrations,
          isDetailedData,
          false,
        )}
        <TableCell aria-hidden="true" />
      </>
    );
  },
};
